import React, { useEffect, useState } from 'react';
import { TrendingDown, Clock, ArrowUp, Plus, Check, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { usePortfolio } from '../hooks/usePortfolio';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function Week52LowsPage() {
  const [lowStocks, setLowStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const { togglePortfolio, isInPortfolio } = usePortfolio();

  useEffect(() => {
    fetch52WeekLows();
  }, []);

  const fetch52WeekLows = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/52week-lows/latest?limit=500`);
      const data = await response.json();
      
      if (data.success) {
        setLowStocks(data.data);
        if (data.last_updated && data.last_updated.timestamp) {
          setLastUpdated(new Date(data.last_updated.timestamp));
        }
      }
    } catch (err) {
      console.error('Error fetching 52-week lows:', err);
      setError('Failed to load 52-week low stocks');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading 52-week lows...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <button onClick={fetch52WeekLows} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-background border-b border-border px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-2xl font-bold font-display">High Potential Stocks</h1>
              <p className="text-sm text-muted-foreground">Stocks down 30%+ from 52-week highs • Recovery opportunities</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                {lastUpdated ? (
                  <span data-testid="last-updated">
                    {formatDistanceToNow(lastUpdated, { addSuffix: true })}
                  </span>
                ) : (
                  <span>Loading...</span>
                )}
              </div>
              <div className="text-xs text-green-500 mt-1">
                Daily at 5 PM AEST
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="px-4 py-4">
        <div className="max-w-7xl mx-auto grid grid-cols-3 gap-3">
          <div className="stats-card">
            <div className="text-2xl font-bold text-yellow-500" data-testid="total-lows">
              {lowStocks.length}
            </div>
            <div className="text-xs text-muted-foreground mt-1">High Potential</div>
          </div>
          <div className="stats-card">
            <div className="text-2xl font-bold text-green-500">
              {lowStocks.length > 0 ? `${Math.round((lowStocks[0].price - lowStocks[0].week_52_low) / lowStocks[0].week_52_low * 100)}%` : '0%'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Closest</div>
          </div>
          <div className="stats-card">
            <div className="text-2xl font-bold text-blue-500">
              {lowStocks.length > 0 ? `${Math.round(lowStocks.reduce((sum, s) => sum + s.upside_to_high_pct, 0) / lowStocks.length)}%` : '0%'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Avg Upside</div>
          </div>
        </div>
        
        {/* Info banner */}
        <div className="max-w-7xl mx-auto mt-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
          <div className="text-xs text-yellow-400">
            🚀 <strong>High Potential Opportunities:</strong> Carefully selected stocks with strong growth potential
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            These stocks show promising indicators and may offer significant upside. Always research fundamentals before investing.
          </div>
        </div>

        {/* Search bar */}
        <div className="max-w-7xl mx-auto mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search ticker or company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:border-emerald-500/50"
            data-testid="week52-search"
          />
        </div>
      </div>

      {/* 52-Week Low Stocks List */}
      <div className="px-4 pb-6">
        <div className="max-w-7xl mx-auto space-y-2">
          {lowStocks
            .filter(stock => {
              if (!search) return true;
              const q = search.toLowerCase();
              return (stock.symbol || '').toLowerCase().includes(q) || (stock.name || '').toLowerCase().includes(q);
            })
            .map((stock, index) => (
            <div
              key={`${stock.symbol}-${stock.as_of}-${index}`}
              className="stock-card"
              data-testid={`low-stock-${stock.symbol}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-muted-foreground">#{index + 1}</span>
                    <h3 className="text-lg font-semibold">{stock.symbol}</h3>
                    {stock.drop_from_high_pct && stock.drop_from_high_pct > 60 && (
                      <span className="badge badge-yellow text-xs">Deep Discount</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{stock.name}</p>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                    <div>
                      <span className="text-muted-foreground">Current:</span>
                      <span className="ml-1 font-semibold">${stock.price.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">52w Low:</span>
                      <span className="ml-1 font-semibold text-yellow-500">${stock.week_52_low ? stock.week_52_low.toFixed(2) : 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">52w High:</span>
                      <span className="ml-1 font-semibold text-blue-400">${stock.week_52_high.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Change:</span>
                      <span className={`ml-1 font-semibold ${stock.change_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {stock.change_pct >= 0 ? '+' : ''}{stock.change_pct.toFixed(2)}%
                      </span>
                    </div>
                  </div>

                  {/* Progress bar showing position between low and high */}
                  <div className="mt-2">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <span>Low</span>
                      <div className="flex-1 bg-border rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-yellow-500 to-blue-500 h-2 rounded-full"
                          style={{
                            width: stock.week_52_low && stock.week_52_high && stock.week_52_high > stock.week_52_low
                              ? `${Math.max(0, Math.min(100, ((stock.price - stock.week_52_low) / (stock.week_52_high - stock.week_52_low)) * 100))}%`
                              : '0%'
                          }}
                        ></div>
                      </div>
                      <span>High</span>
                    </div>
                  </div>
                </div>
                
                {/* Add to Portfolio Button - Centered */}
                <button
                  onClick={(e) => { e.stopPropagation(); togglePortfolio(stock, 'High Potential'); }}
                  className={`mx-4 p-3 rounded-full transition-all ${
                    isInPortfolio(stock.symbol)
                      ? 'bg-green-500/30 text-green-400 hover:bg-red-500/30 hover:text-red-400'
                      : 'bg-card-hover hover:bg-primary/20 text-muted-foreground hover:text-primary'
                  }`}
                  data-testid={`add-portfolio-${stock.symbol}`}
                  title={isInPortfolio(stock.symbol) ? 'Remove from Portfolio' : 'Add to Portfolio'}
                >
                  {isInPortfolio(stock.symbol) ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Plus className="w-5 h-5" />
                  )}
                </button>
                
                <div className="text-right min-w-[80px]">
                  <div className="text-2xl font-bold text-red-500" data-testid={`distance-${stock.symbol}`}>
                    -{stock.drop_from_high_pct ? stock.drop_from_high_pct.toFixed(1) : '0.0'}%
                  </div>
                  <div className="text-xs text-muted-foreground">from high</div>
                  
                  <div className="mt-2 flex items-center justify-end gap-1 text-sm text-green-500">
                    <ArrowUp className="w-4 h-4" />
                    <span className="font-semibold">{stock.upside_to_high_pct ? stock.upside_to_high_pct.toFixed(0) : '0'}%</span>
                  </div>
                  <div className="text-xs text-muted-foreground">to high</div>
                </div>
              </div>
            </div>
          ))}
          
          {lowStocks.length === 0 && (
            <div className="text-center py-12">
              <TrendingDown className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No 52-week low data available</p>
              <p className="text-sm text-muted-foreground mt-2">Check back after 5 PM AEST</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Week52LowsPage;
